import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiInstance } from '../../axiosApi';

const initialState = {
  processes: [],
  dashboard: {},
  activeCalls: [],
  liveData: { ringing_data: [], waiting_data: [] },
  transcript: null,
  loading: false,
  error: null
};

export const aiProcessListGet = createAsyncThunk('aiReport/processList', async () => {
  const response = await apiInstance.get('/ai-real-time/process');
  return response;
});

export const aiCountGet = createAsyncThunk('aiReport/count', async (payload) => {
  const response = await apiInstance.get('/ai-real-time/count', { params: payload });
  return response;
});

export const aiListGet = createAsyncThunk('aiReport/list', async (payload) => {
  const response = await apiInstance.get('/ai-real-time/list', { params: payload });
  return response;
});

export const aiTranscriptGet = createAsyncThunk('aiReport/transcript', async (called) => {
  const response = await apiInstance.get(`/callex-call/${called}/transcript`);
  return response;
});

export const aiLiveDataGet = createAsyncThunk('aiReport/liveData', async () => {
  const response = await apiInstance.get('/ai-real-time/live-data');
  return response;
});

export const aiClearBuffer = createAsyncThunk('aiReport/clearBuffer', async () => {
  const response = await apiInstance.get('/ai-real-time/clear-buffer');
  return response;
});

const aiRealtimeReportSlice = createSlice({
  name: 'aiRealtimeReport',
  initialState,
  reducers: {
    clearAiTranscript: (state) => {
      state.transcript = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(aiProcessListGet.fulfilled, (state, action) => {
        state.processes = action.payload?.data || [];
      })
      .addCase(aiProcessListGet.rejected, (state) => {
        state.processes = [];
      })
      .addCase(aiCountGet.pending, (state) => {
        state.loading = true;
      })
      .addCase(aiCountGet.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload?.data || {};
      })
      .addCase(aiCountGet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(aiListGet.fulfilled, (state, action) => {
        state.activeCalls = action.payload?.data || [];
      })
      .addCase(aiListGet.rejected, (state) => {
        state.activeCalls = [];
      })
      .addCase(aiTranscriptGet.fulfilled, (state, action) => {
        state.transcript = action.payload?.data || null;
      })
      .addCase(aiLiveDataGet.fulfilled, (state, action) => {
        state.liveData = {
          ringing_data: action.payload?.data?.ringing_data || [],
          waiting_data: action.payload?.data?.waiting_data || []
        };
      })
      .addCase(aiLiveDataGet.rejected, (state) => {
        state.liveData = { ringing_data: [], waiting_data: [] };
      });
  }
});

export const { clearAiTranscript } = aiRealtimeReportSlice.actions;

export const selectAiProcesses = (state) => state.aiRealtimeReport.processes;
export const useAiProcesses = () => {
  const processes = useSelector(selectAiProcesses);
  return useMemo(() => processes, [processes]);
};

export const selectAiDashboard = (state) => state.aiRealtimeReport.dashboard;
export const useAiDashboard = () => {
  const dashboard = useSelector(selectAiDashboard);
  return useMemo(() => dashboard, [dashboard]);
};

export const selectAiActiveCalls = (state) => state.aiRealtimeReport.activeCalls;
export const useAiActiveCalls = () => {
  const activeCalls = useSelector(selectAiActiveCalls);
  return useMemo(() => activeCalls, [activeCalls]);
};

export const selectAiTranscript = (state) => state.aiRealtimeReport.transcript;
export const useAiTranscript = () => {
  const transcript = useSelector(selectAiTranscript);
  return useMemo(() => transcript, [transcript]);
};

export const selectAiLiveData = (state) => state.aiRealtimeReport.liveData;
export const useAiLiveData = () => {
  const liveData = useSelector(selectAiLiveData);
  return useMemo(() => liveData, [liveData]);
};

export const selectAiReportLoading = (state) => state.aiRealtimeReport.loading;
export const useAiReportLoading = () => {
  const loading = useSelector(selectAiReportLoading);
  return useMemo(() => loading, [loading]);
};

export default aiRealtimeReportSlice.reducer;
